import React, { useEffect, useState } from 'react';
import { InitialStep } from './InitialStep';
import { FinalStep } from './FinalStep';
import { FeedbackStep } from './FeedbackStep';
import {
  CancelationReason,
  INVOICE_TYPE_ISSUED,
  PAUSE_SUBSCRIPTION_DISABLED,
  PauseSubscriptionDetails,
} from '../../types';
import { BenefitsStep } from './BenefitsStep';
import { useTimelineData } from '../../hooks/useTimelineData';
import { BoxSpinner } from '../../../commons/components/Spinner';
import { PauseStep } from './PauseStep';
import { useGradualRollout } from '../../../commons/hooks/useGradualRollout';
import { billingPortalConfig } from '../../../commons/config';
import useApi from '../../../api/backendApiContext';

type WizardSteps = 'preparation' | 'benefits' | 'initial' | 'pause' | 'feedback' | 'final';

export const CancellationWizard = () => {
  const [step, setStep] = useState<WizardSteps | null>('preparation');
  const [hasBenefitsStep, setHasBenefitsStep] = useState(false);
  const [pauseDetails, setPauseDetails] = useState<PauseSubscriptionDetails | null>(null);
  const { billingApi } = useApi();

  const [isInitialized, setIsInitialized] = useState(false);
  const timelineData = useTimelineData();
  const { isEnabled: pauseFeatureIsEnabled } = useGradualRollout(
    'pause-cancellation-step',
    billingPortalConfig.pauseOnCancellationRolloutLevel
  );
  useEffect(() => {
    if (!pauseFeatureIsEnabled) {
      setPauseDetails(PAUSE_SUBSCRIPTION_DISABLED);
    } else {
      // noinspection JSIgnoredPromiseFromCall
      billingApi
        .getPauseSubscriptionDetails()
        .then((x) => setPauseDetails(x))
        .catch(() => setPauseDetails(PAUSE_SUBSCRIPTION_DISABLED));
    }
  }, [billingApi, pauseFeatureIsEnabled]);
  useEffect(() => {
    if (isInitialized) return;
    if (step === 'preparation' && timelineData.type === 'data' && pauseDetails != null) {
      setIsInitialized(true);

      const issuedInvoices = timelineData.data.invoicesAndBenefits.filter(
        (x) => typeof x.invoice === 'object' && x.invoice?.type === INVOICE_TYPE_ISSUED
      );
      if (timelineData.data.billingPlan.name === 'month' && issuedInvoices.length > 2) {
        setHasBenefitsStep(true);
        setStep('benefits');
      } else {
        setHasBenefitsStep(false);
        setStep('initial');
      }
    }
  }, [timelineData, step, pauseDetails, isInitialized, pauseFeatureIsEnabled]);

  const [selectedReasons, setSelectedReasons] = useState<CancelationReason[]>([]);
  const [comment, setComment] = useState('');
  const [closed, setClosed] = useState(false);
  if (closed) return null;

  return (
    <>
      {step === 'preparation' && (timelineData.type === 'component' || !pauseDetails) && (
        <BoxSpinner />
      )}
      {step === 'benefits' && (
        <BenefitsStep onNext={() => setStep('initial')} onCancel={() => setStep(null)} />
      )}
      {step === 'initial' && (
        <InitialStep
          hasPreviousStep={hasBenefitsStep}
          onNext={() => setStep(pauseDetails?.canPause ? 'pause' : 'feedback')}
          onGoBackClicked={() => setStep(hasBenefitsStep ? 'benefits' : null)}
        />
      )}
      {step === 'pause' && pauseDetails && pauseDetails.canPause && (
        <PauseStep
          pauseDetails={pauseDetails}
          onNext={() => setStep('feedback')}
          onGoBackClicked={() => setStep('initial')}
          onClosed={() => setClosed(true)}
        />
      )}
      {step === 'feedback' && (
        <FeedbackStep
          selectedReasons={selectedReasons}
          onSelectedReasonsChanged={setSelectedReasons}
          comment={comment}
          onCommentChanged={setComment}
          onGoBackClicked={() => setStep(pauseDetails?.canPause ? 'pause' : 'initial')}
          onBillingPlanCanceled={() => setStep('final')}
        />
      )}
      {step === 'final' && <FinalStep />}
    </>
  );
};
